// "Preview set" in the Set_Proposal review (blueprint-sets Req 11.1, 11.2, 11.5, 11.6; design "Set preview in the
// review"). The preview flow of create-doc-interactive (`usePreviewOf`, features/preview/previewFlow.ts) over `api.createSetPreview`;
// the basis key is `canonicalJson(toPreviewRequest(state))`, so any change to the included types, their metadata, or
// their fields marks the shown preview out of date (Req 11.5). Wired into `SetProposalReview`'s `preview` slot.
import { useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { canonicalJson, SAMPLE_EXPIRED_TEXT } from '@bda/shared';
import { api } from '../../api/client';
import { useRun, useRunResult } from '../../api/hooks';
import { runtimeConfig } from '../../config';
import { safeName } from '../../lib/download';
import { previewStatus } from '../preview/previewFlow';
import { createBlockers, toPreviewRequest, type CreateSetPreviewRequest, type ReviewState } from './proposalState';
import { DocumentViewer } from '../../components/DocumentViewer';
import { PreviewProgress, STARTING_TEXT, usePreviewOf, type UsePreviewOf } from '../preview/PreviewPanel';
import type { SetProposalReviewProps } from './SetProposalReview';
import { previewPagesText } from '../blueprints/SuggestModal';
import { PREVIEW_SET_LABEL, SetResultView } from './SetResultView';

export const SET_PREVIEW_STALE_TEXT =
  'Out of date: the proposal changed. Preview the set again to see the current proposal.';
export const SET_PREVIEW_ID = 'set-proposal-preview';

/** The basis key of a review state: what "Preview set" would send now (Req 11.5). */
export const setPreviewBasis = (state: ReviewState): string => canonicalJson(toPreviewRequest(state));

/**
 * Why "Preview set" is unavailable, or undefined. The preview request carries no set name or description, so only
 * the blockers that make its members invalid count: no included type, too many, duplicate types, invalid types.
 */
export function previewBlockedReason(state: ReviewState, maxMembers: number, busy: boolean): string | undefined {
  // Neutral set metadata: blockers about the set's own name and description do not apply to a preview.
  const problems = createBlockers({ ...state, name: 'Preview', description: '' }, maxMembers);
  if (problems.length === 1) return `Fix this first: ${problems[0]!.message}`;
  if (problems.length > 1) return `Fix ${problems.length} problems first (listed under "Create set is unavailable").`;
  return busy ? STARTING_TEXT : undefined;
}

function PreviewSetAction({
  preview,
  state,
  maxMembers,
}: {
  preview: UsePreviewOf<CreateSetPreviewRequest>;
  state: ReviewState;
  maxMembers: number;
}) {
  const { busy } = previewStatus(preview.state, '');
  const expired = preview.state.attempt?.phase === 'rejected' && preview.state.attempt.error.kind === 'expired';
  const reason = expired ? SAMPLE_EXPIRED_TEXT : previewBlockedReason(state, maxMembers, busy);
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Box color="text-body-secondary" padding={{ top: 'xxs' }}>
        <span id={`${SET_PREVIEW_ID}-pages`}>{previewPagesText(state.pages)}</span>
      </Box>
      <Button
        disabled={reason !== undefined}
        disabledReason={reason}
        ariaDescribedby={`${SET_PREVIEW_ID}-pages`}
        onClick={() => preview.send({ ...toPreviewRequest(state), basisKey: setPreviewBasis(state) })}
      >
        Preview set
      </Button>
    </SpaceBetween>
  );
}

/** The shown Set_Preview_Run: the packet beside the set Result_View labeled "Preview: set not saved" (Req 11.1). */
function ShownSetPreview({ runId, stale }: { runId: string; stale: boolean }) {
  const run = useRun(runId);
  const result = useRunResult(runId, true);
  const [goTo, setGoTo] = useState<{ page: number; seq: number } | undefined>();
  const data = result.data && !result.data.expired ? result.data : null;
  const r = run.data;
  return (
    <Container
      header={
        <Header variant="h2" actions={<Badge color="blue">{PREVIEW_SET_LABEL}</Badge>}>
          Set preview
        </Header>
      }
    >
      <SpaceBetween size="l">
        {stale && <Alert type="warning">{SET_PREVIEW_STALE_TEXT}</Alert>}
        {result.error && <Alert type="error">The preview result could not be loaded.</Alert>}
        {result.data?.expired && <Alert type="info">The preview result has expired.</Alert>}
        {data && !data.set && <Alert type="error">The preview result has no set snapshot.</Alert>}
        {data?.set && r ? (
          <Grid gridDefinition={[{ colspan: { default: 12, l: 5 } }, { colspan: { default: 12, l: 7 } }]}>
            <DocumentViewer url={data.documentUrl} contentType={r.contentType} name={r.fileName} goTo={goTo} />
            <SetResultView
              result={data.result}
              snapshot={data.set}
              baseName={safeName(`set-preview-${r.fileName}`)}
              label="preview"
              contentType={r.contentType}
              onShowPage={
                r.contentType === 'application/pdf'
                  ? (page) => setGoTo((g) => ({ page, seq: (g?.seq ?? 0) + 1 }))
                  : undefined
              }
            />
          </Grid>
        ) : (
          !result.error && !result.data?.expired && !data && <Spinner />
        )}
      </SpaceBetween>
    </Container>
  );
}

function SetPreviewPanel({ preview, state }: { preview: UsePreviewOf<CreateSetPreviewRequest>; state: ReviewState }) {
  const { shown } = preview.state;
  const stale = previewStatus(preview.state, setPreviewBasis(state)).stale;
  if (!preview.state.attempt && !shown) return null;
  return (
    <div id={SET_PREVIEW_ID}>
      <SpaceBetween size="m">
        <PreviewProgress state={preview.state} retry={preview.send} />
        {shown && <ShownSetPreview key={shown.runId} runId={shown.runId} stale={stale} />}
      </SpaceBetween>
    </div>
  );
}

/**
 * The `preview` slot of `SetProposalReview`: "Preview set" next to "Create set" and the preview below the page lists.
 * Call it unconditionally at the top of the page (it holds the preview state across the review's renders).
 */
export function useSetProposalPreview(maxMembers?: number): NonNullable<SetProposalReviewProps['preview']> {
  const preview = usePreviewOf<CreateSetPreviewRequest>(api.createSetPreview, { enabled: true });
  const max = maxMembers ?? runtimeConfig().sets.maxSetMembers;
  return {
    action: (state: ReviewState) => <PreviewSetAction preview={preview} state={state} maxMembers={max} />,
    panel: (state: ReviewState) => <SetPreviewPanel preview={preview} state={state} />,
  };
}
